import { useEffect } from 'react';
import { bus, refetchRecordingsList, siteLastDataRefreshed } from '@hotjar/webapp/utils/events';
import { SessionStorage } from '@hotjar/insights-common/lib/storage-factory';
import { SharedSessionStorage } from '@hotjar/webapp/utils/shared-session-storage';
import { RecordingPlaybackTracker } from '@hotjar/webapp/recordings/recording-playback-tracker';
import { getState } from '@hotjar/webapp/domains/activation/tracking-code';
import {
  getSortingPreferences,
  initialize,
  deleteDeprecatedConfigColumnsVisibilityRecord,
  getHotjarFiltersPreferences,
  setColumnsVisibility,
  reloadRecordings,
} from '../utils';
import type { Store } from '../../../store/list/types';

export const useInitialization = (store: Store): void => {
  useEffect(() => {
    initialize(store);
    setColumnsVisibility(store);
    getHotjarFiltersPreferences(store);
    getSortingPreferences(store);
    deleteDeprecatedConfigColumnsVisibilityRecord();

    SharedSessionStorage.onChange('viewedRecordings', function (nRecordings) {
      RecordingPlaybackTracker.triggerIfEnoughViewed(nRecordings as number);
    });

    /**
     * When refetch list event is triggered, we need to refetch recordings list
     */
    const unsubscribeRefetchRecordingsList = bus.subscribe(refetchRecordingsList, () => {
      reloadRecordings(store);
    });

    /**
     * Listen to siteLastDataRefreshed and update the model as needed.
     */
    const unsubscribeSiteLastDataRefreshed = bus.subscribe(siteLastDataRefreshed, ({ payload }) => {
      const { setTrackingCode, setSite } = store.getState();

      setSite(payload.site);
      setTrackingCode(getState(payload.state.last_data));
    });

    /**
     * Remove the session storage variables set by hotjar-xx.js by the playback module.
     *
     * If insights runs on the same origin as the recording these values will otherwise remain
     * available when the polls / RUT module loads its preview and will cause it to (incorrectly)
     * enter playback mode.
     */
    return () => {
      SessionStorage.remove('_hjnodatapush');
      SessionStorage.remove('_hjplayback');
      unsubscribeRefetchRecordingsList();
      unsubscribeSiteLastDataRefreshed();
    };

    // We just want to run this effect on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
